import { UsStatesEnumType } from "@libs/api/patients";
import { useClinicsQuery } from "@/data/queries/clinicsQueries";
import { useCurrentHouseholdMemberQuery } from "@/data/queries/householdMembersQueries";

/**
 * Resolves the US state for the current patient.
 *
 * Priority:
 * 1. Home address state
 * 2. Mailing address state
 * 3. First connected clinic state (after clinics have loaded)
 * 4. Defaults to "CA" (after clinics have loaded)
 *
 * Note: returns `undefined` while clinics are loading to avoid briefly resolving
 * to the wrong state.
 */
export const usePatientUSState = (): UsStatesEnumType | undefined => {
  const { data: currentUserHouseholdMember } = useCurrentHouseholdMemberQuery();
  const { data: userConnectedClinics, isLoading: isClinicsLoading } =
    useClinicsQuery();

  const addressState =
    currentUserHouseholdMember?.mainAddressState ??
    currentUserHouseholdMember?.mailingAddressState;

  if (addressState) {
    return addressState;
  }

  // Defer fallback to clinic state / "CA" until the clinics query has resolved,
  // so we don't briefly return the wrong state.
  if (isClinicsLoading) {
    return undefined;
  }

  return userConnectedClinics?.[0]?.states[0] ?? "CA";
};
